import { useEffect, useState } from "react";

const emptyForm = {
  category: "Performance Mods",
  partName: "",
  brand: "",
  partNumber: "",
  installedDate: "",
  installedMileage: "",
  price: "",
  laborCost: "",
  installedBy: "Owner",
  installerName: "",
  link: "",
  notes: ""
};

const installedByOptions = [
  "Owner",
  "Independent Shop",
  "Dealership",
  "Other"
];

function AddModModal({
  isOpen,
  modCategories = [],
  editingModId,
  initialMod,
  onSave,
  onCancel,
  isSaving = false
}) {
  const [form, setForm] = useState(emptyForm);
  const [formError, setFormError] = useState("");

  useEffect(() => {
    if (!isOpen) return;

    if (initialMod) {
      setForm({
        category: initialMod.category || "Performance Mods",
        partName: initialMod.partName || "",
        brand: initialMod.brand || "",
        partNumber: initialMod.partNumber || "",
        installedDate: initialMod.installedDate || "",
        installedMileage: initialMod.installedMileage ?? "",
        price: initialMod.price ?? "",
        laborCost: initialMod.laborCost ?? "",
        installedBy: initialMod.installedBy || "Owner",
        installerName: initialMod.installerName || "",
        link: initialMod.link || "",
        notes: initialMod.notes || ""
      });
    } else {
      setForm({
        ...emptyForm,
        category: modCategories[0] || "Performance Mods"
      });
    }


    setFormError("");
  }, [isOpen, initialMod, modCategories]);

  if (!isOpen) return null;

  const updateField = (field, value) => {
    setForm((current) => ({
      ...current,
      [field]: value
    }));
  };

  const toNumberOrNull = (value) => {
    if (value === "" || value === null || value === undefined) return null;
    return Number(value);
  };

  const formatPreviewCost = (value) => {
    if (value === null || value === undefined || value === "") return "$0.00";

    const numberValue = Number(value);

    if (Number.isNaN(numberValue)) return "$0.00";

    return `$${numberValue.toFixed(2)}`;
  };

  const handleSave = () => {
    setFormError("");

    if (!form.category) {
      setFormError("Choose a mod category.");
      return;
    }

    if (!form.partName.trim()) {
      setFormError("Enter a part name.");
      return;
    }

    const numericFields = [
      ["Installed mileage", form.installedMileage],
      ["Part price", form.price],
      ["Labor cost", form.laborCost]
    ];

    for (const [label, value] of numericFields) {
      if (value !== "" && Number(value) < 0) {
        setFormError(`${label} cannot be negative.`);
        return;
      }
    }

    if (form.link) {
      try {
        new URL(form.link);
      } catch {
        setFormError("Enter a valid link.");
        return;
      }
    }

    const price = toNumberOrNull(form.price);
    const laborCost = toNumberOrNull(form.laborCost);

    const totalCost =
      price !== null && laborCost !== null
        ? price + laborCost
        : price !== null
        ? price
        : laborCost !== null
        ? laborCost
        : null;

    const payload = {
      category: form.category,
      partName: form.partName.trim(),
      brand: form.brand.trim() || null,
      partNumber: form.partNumber.trim() || null,
      installedDate: form.installedDate || null,
      installedMileage: toNumberOrNull(form.installedMileage),
      price,
      laborCost,
      totalCost,
      installedBy: form.installedBy,
      installerName: form.installerName.trim() || null,
      link: form.link.trim() || null,
      notes: form.notes.trim() || null
    };

    onSave(payload);
  };

  const pricePreview = toNumberOrNull(form.price);
  const laborPreview = toNumberOrNull(form.laborCost);
  const totalPreview =
    pricePreview !== null && laborPreview !== null
      ? pricePreview + laborPreview
      : pricePreview !== null
      ? pricePreview
      : laborPreview !== null
      ? laborPreview
      : 0;

  return (
    <div style={overlayStyle}>
      <div style={modalStyle}>
        <h2 style={{ margin: 0 }}>
          {editingModId ? "Edit Mod" : "Add Mod"}
        </h2>

        {formError && <div style={errorStyle}>{formError}</div>}

        <section style={sectionStyle}>
          <h3 style={sectionTitleStyle}>Mod Details</h3>

          <label style={labelStyle}>Category</label>
          <select
            value={form.category}
            onChange={(e) => updateField("category", e.target.value)}
            style={inputStyle}
          >
            {modCategories.map((category) => (
              <option key={category} value={category}>
                {category}
              </option>
            ))}
          </select>

          <label style={labelStyle}>Part Name</label>
          <input
            placeholder="Example: Tomei Exhaust"
            value={form.partName}
            onChange={(e) => updateField("partName", e.target.value)}
            style={inputStyle}
          />

          <label style={labelStyle}>Brand</label>
          <input
            placeholder="Example: Tomei"
            value={form.brand}
            onChange={(e) => updateField("brand", e.target.value)}
            style={inputStyle}
          />

          <label style={labelStyle}>Part Number</label>
          <input
            placeholder="Manufacturer part number"
            value={form.partNumber}
            onChange={(e) => updateField("partNumber", e.target.value)}
            style={inputStyle}
          />
        </section>

        <section style={sectionStyle}>
          <h3 style={sectionTitleStyle}>Install</h3>

          <label style={labelStyle}>Install Date</label>
          <input
            type="date"
            value={form.installedDate}
            onChange={(e) => updateField("installedDate", e.target.value)}
            style={inputStyle}
          />

          <input
            type="number"
            min="0"
            placeholder="Install Mileage - optional"
            value={form.installedMileage}
            onChange={(e) => updateField("installedMileage", e.target.value)}
            style={inputStyle}
          />

          <label style={labelStyle}>Installed By</label>
          <select
            value={form.installedBy}
            onChange={(e) => updateField("installedBy", e.target.value)}
            style={inputStyle}
          >
            {installedByOptions.map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </select>

          <input
            placeholder="Installer or shop name - optional"
            value={form.installerName}
            onChange={(e) => updateField("installerName", e.target.value)}
            style={inputStyle}
          />
        </section>

        <section style={sectionStyle}>
          <h3 style={sectionTitleStyle}>Cost</h3>

          <input
            type="number"
            min="0"
            step="0.01"
            placeholder="Part Price - optional"
            value={form.price}
            onChange={(e) => updateField("price", e.target.value)}
            style={inputStyle}
          />

          <input
            type="number"
            min="0"
            step="0.01"
            placeholder="Labor Cost - optional"
            value={form.laborCost}
            onChange={(e) => updateField("laborCost", e.target.value)}
            style={inputStyle}
          />

          <div style={summaryCostStyle}>
            <span>Total Cost</span>
            <strong>{formatPreviewCost(totalPreview)}</strong>
          </div>
        </section>

        <section style={sectionStyle}>
          <h3 style={sectionTitleStyle}>Documentation</h3>

          <input
            type="url"
            placeholder="Product or reference link - optional"
            value={form.link}
            onChange={(e) => updateField("link", e.target.value)}
            style={inputStyle}
          />

          <textarea
            placeholder="Notes - optional"
            value={form.notes}
            onChange={(e) => updateField("notes", e.target.value)}
            style={{ ...inputStyle, height: "110px" }}
          />
        </section>

        <div style={buttonRowStyle}>
          <button
            type="button"
            onClick={onCancel}
            style={cancelButtonStyle}
            disabled={isSaving}
          >
            Cancel
          </button>

          <button
            type="button"
            onClick={handleSave}
            style={saveButtonStyle}
            disabled={isSaving}
          >
            {isSaving
              ? "Saving..."
              : editingModId
              ? "Save Changes"
              : "Add Mod"}
          </button>
        </div>
      </div>
    </div>
  );
}

const overlayStyle = {
  position: "fixed",
  inset: 0,
  backgroundColor: "rgba(0,0,0,0.82)",
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  zIndex: 30000,
  padding: "20px",
  boxSizing: "border-box",
  overflowY: "auto"
};

const modalStyle = {
  backgroundColor: "#181818",
  border: "1px solid #333",
  borderRadius: "20px",
  padding: "22px",
  maxWidth: "460px",
  width: "100%",
  maxHeight: "92vh",
  overflowY: "auto",
  boxSizing: "border-box",
  color: "white",
  display: "flex",
  flexDirection: "column",
  gap: "16px"
};

const sectionStyle = {
  display: "flex",
  flexDirection: "column",
  gap: "10px"
};

const sectionTitleStyle = {
  margin: "4px 0",
  fontSize: "14px",
  textTransform: "uppercase",
  letterSpacing: "0.6px",
  color: "#e10600"
};

const inputStyle = {
  padding: "14px",
  borderRadius: "12px",
  border: "1px solid #2a2a2a",
  backgroundColor: "#1c1c1c",
  color: "white",
  boxSizing: "border-box",
  width: "100%",
  fontSize: "16px"
};

const labelStyle = {
  display: "block",
  fontSize: "12px",
  fontWeight: "600",
  color: "#fff"
};

const errorStyle = {
  backgroundColor: "#3a1111",
  border: "1px solid #7a1b1b",
  color: "white",
  borderRadius: "12px",
  padding: "12px",
  fontSize: "14px"
};

const summaryCostStyle = {
  backgroundColor: "#1f1f1f",
  border: "1px solid #2a2a2a",
  borderRadius: "12px",
  padding: "14px",
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  fontSize: "16px"
};

const buttonRowStyle = {
  display: "flex",
  gap: "10px",
  marginTop: "4px"
};

const cancelButtonStyle = {
  flex: 1,
  backgroundColor: "#333",
  color: "white",
  border: "none",
  borderRadius: "12px",
  padding: "14px",
  fontWeight: "bold",
  cursor: "pointer"
};

const saveButtonStyle = {
  flex: 1,
  backgroundColor: "#e10600",
  color: "white",
  border: "none",
  borderRadius: "12px",
  padding: "14px",
  fontWeight: "bold",
  cursor: "pointer"
};

export default AddModModal;